import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Flag, Users, Search, Loader2, Save, RotateCcw, ChevronRight, 
  Eye, EyeOff, FlaskConical, Check, X, History, Clock, User,
  LayoutGrid, Lightbulb, Sparkles, Building2, Link2, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import api from '../services/api';

// Category display config
const CATEGORY_CONFIG = {
  product_tabs: { label: 'Product Tabs', icon: LayoutGrid, description: 'Control visibility of tabs in product detail view' },
  ideation_tools: { label: 'Ideation Tools', icon: Lightbulb, description: 'Control visibility of individual ideation tools' },
  copilots: { label: 'Co-Pilots', icon: Sparkles, description: 'Control visibility of AI co-pilots across the platform' },
  org_nav: { label: 'Organization Navigation', icon: Building2, description: 'Control visibility of org-level navigation items (incl. Team)' },
  integrations: { label: 'Integrations', icon: Link2, description: 'Control product-level integration connections' },
  help: { label: 'Help & Docs', icon: BookOpen, description: 'Control documentation and help access' },
};

// Status badge colors
const STATUS_COLORS = {
  on: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  beta: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  off: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const STATUS_LABELS = {
  on: 'On',
  beta: 'Beta',
  off: 'Off',
};

const AdminFeaturesPage = () => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Feature flags state
  const [flags, setFlags] = useState([]);
  const [groupedFlags, setGroupedFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // feature_id being saved
  
  // History state
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Beta testers state
  const [betaTesters, setBetaTesters] = useState([]);
  const [betaSearch, setBetaSearch] = useState('');
  const [betaFilter, setBetaFilter] = useState('all');
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaTotal, setBetaTotal] = useState(0);
  const [betaCount, setBetaCount] = useState(0);
  const [togglingUser, setTogglingUser] = useState(null);
  
  // Load feature flags
  const loadFlags = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/feature-flags');
      setFlags(response.data.flags || []);
      setGroupedFlags(response.data.grouped || {});
    } catch (err) {
      toast.error('Failed to load feature flags');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load history
  const loadHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get('/api/admin/feature-flags/history');
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);
  
  // Load beta testers
  const loadBetaTesters = useCallback(async () => {
    try {
      setBetaLoading(true);
      const params = new URLSearchParams();
      if (betaSearch) params.append('search', betaSearch);
      if (betaFilter !== 'all') params.append('filter_status', betaFilter);
      params.append('limit', '100');
      
      const response = await api.get(`/api/admin/feature-flags/beta-testers?${params.toString()}`);
      setBetaTesters(response.data.users || []);
      setBetaTotal(response.data.total || 0);
      setBetaCount(response.data.beta_count || 0);
    } catch (err) {
      toast.error('Failed to load users');
      console.error(err);
    } finally {
      setBetaLoading(false);
    }
  }, [betaSearch, betaFilter]);
  
  useEffect(() => {
    loadFlags();
    loadHistory();
  }, [loadFlags, loadHistory]);
  
  useEffect(() => {
    loadBetaTesters();
  }, [loadBetaTesters]);
  
  // Update flag status
  const updateFlagStatus = async (featureId, newStatus) => {
    try {
      setSaving(featureId);
      await api.put(`/api/admin/feature-flags/${featureId}`, { status: newStatus });
      
      // Update local state
      setFlags(prev => prev.map(f => 
        f.feature_id === featureId ? { ...f, status: newStatus } : f
      ));
      setGroupedFlags(prev => {
        const updated = { ...prev };
        for (const cat in updated) {
          updated[cat] = updated[cat].map(f =>
            f.feature_id === featureId ? { ...f, status: newStatus } : f
          );
        }
        return updated;
      });
      
      toast.success(`Updated ${featureId} to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update feature flag');
      console.error(err);
    } finally {
      setSaving(null);
    }
  };
  
  // Save version
  const saveVersion = async () => {
    try {
      const response = await api.post('/api/admin/feature-flags/save-version');
      toast.success(`Saved as version ${response.data.version}`);
      loadHistory();
    } catch (err) {
      toast.error('Failed to save version');
      console.error(err);
    }
  };
  
  // Rollback to version
  const rollbackToVersion = async (version) => {
    if (!window.confirm(`Roll back to version ${version}? Current settings will be auto-saved first.`)) {
      return;
    }
    
    try {
      await api.post(`/api/admin/feature-flags/rollback/${version}`);
      toast.success(`Rolled back to version ${version}`);
      loadFlags();
      loadHistory();
    } catch (err) {
      toast.error('Failed to rollback');
      console.error(err);
    }
  };
  
  // Toggle beta tester status
  const toggleBetaTester = async (userId, currentStatus) => {
    try {
      setTogglingUser(userId);
      await api.put(`/api/admin/feature-flags/beta-testers/${userId}`, { 
        is_beta_tester: !currentStatus 
      });
      
      // Update local state
      setBetaTesters(prev => prev.map(u =>
        u.user_id === userId ? { ...u, is_beta_tester: !currentStatus } : u
      ));
      setBetaCount(prev => currentStatus ? prev - 1 : prev + 1);
      
      toast.success(currentStatus ? 'Removed from beta testers' : 'Added to beta testers');
    } catch (err) {
      toast.error('Failed to update beta status');
      console.error(err);
    } finally {
      setTogglingUser(null);
    }
  };
  
  // Render feature flag row
  const FeatureFlagRow = ({ flag }) => (
    <div className={cn(
      "flex items-center justify-between p-3 rounded-lg border",
      colors.bgCard, colors.borderPrimary
    )}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn("font-medium text-sm", colors.textPrimary)}>
            {flag.feature_name}
          </span>
          {flag.group && (
            <Badge variant="outline" className="text-[10px]">
              {flag.group}
            </Badge>
          )}
        </div>
        <p className={cn("text-xs mt-0.5 truncate", colors.textMuted)}>
          {flag.description}
        </p>
      </div>
      
      <div className="flex items-center gap-2 ml-4">
        <Select
          value={flag.status}
          onValueChange={(value) => updateFlagStatus(flag.feature_id, value)}
          disabled={saving === flag.feature_id}
        >
          <SelectTrigger className="w-[100px] h-8">
            {saving === flag.feature_id ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <SelectValue />
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="on">
              <div className="flex items-center gap-2">
                <Eye className="w-3 h-3 text-green-500" />
                <span>On</span>
              </div>
            </SelectItem>
            <SelectItem value="beta">
              <div className="flex items-center gap-2">
                <FlaskConical className="w-3 h-3 text-purple-500" />
                <span>Beta</span>
              </div>
            </SelectItem>
            <SelectItem value="off">
              <div className="flex items-center gap-2">
                <EyeOff className="w-3 h-3 text-red-500" />
                <span>Off</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
  
  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Features" subtitle="Control feature visibility across the platform" />
        
        <main className="flex-1 overflow-y-auto p-6">
          <Tabs defaultValue="flags" className="space-y-6">
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="flags" className="gap-2">
                  <Flag className="w-4 h-4" />
                  Feature Flags
                </TabsTrigger>
                <TabsTrigger value="testers" className="gap-2">
                  <Users className="w-4 h-4" />
                  Beta Testers
                  {betaCount > 0 && (
                    <Badge variant="secondary" className="ml-1">{betaCount}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              <Button onClick={saveVersion} variant="outline" size="sm" className="gap-2">
                <Save className="w-4 h-4" />
                Save Version
              </Button>
            </div>
            
            {/* Feature Flags Tab */}
            <TabsContent value="flags" className="space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Feature Categories */}
                  {Object.entries(CATEGORY_CONFIG).map(([category, config]) => (
                    <Card key={category} className={cn("border", colors.bgCard, colors.borderPrimary)}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-2">
                          <config.icon className={cn("w-5 h-5", colors.accent)} />
                          <CardTitle className={cn("text-base", colors.textPrimary)}>{config.label}</CardTitle>
                        </div>
                        <CardDescription className={cn("text-xs", colors.textMuted)}>
                          {config.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {(groupedFlags[category] || []).map(flag => (
                          <FeatureFlagRow key={flag.feature_id} flag={flag} />
                        ))}
                        {(!groupedFlags[category] || groupedFlags[category].length === 0) && (
                          <p className={cn("text-sm text-center py-4", colors.textMuted)}>
                            No features in this category
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Version History Card */}
                  <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <History className={cn("w-5 h-5", colors.accent)} />
                        <CardTitle className={cn("text-base", colors.textPrimary)}>Version History</CardTitle>
                      </div>
                      <CardDescription className={cn("text-xs", colors.textMuted)}>
                        Rollback to previous configurations
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {loadingHistory ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </div>
                      ) : history.length === 0 ? (
                        <p className={cn("text-sm text-center py-4", colors.textMuted)}>
                          No versions saved yet. Click "Save Version" to create a snapshot.
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {history.map((version) => (
                            <div
                              key={version.version}
                              className={cn(
                                "flex items-center justify-between p-2 rounded border",
                                colors.bgSecondary, colors.borderPrimary
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <Badge variant="outline">v{version.version}</Badge>
                                <div>
                                  <p className={cn("text-xs", colors.textPrimary)}>
                                    {version.created_by_name}
                                  </p>
                                  <p className={cn("text-[10px]", colors.textMuted)}>
                                    {new Date(version.created_at).toLocaleString()}
                                  </p>
                                  {version.note && (
                                    <p className={cn("text-[10px] italic", colors.textMuted)}>
                                      {version.note}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => rollbackToVersion(version.version)}
                                className="h-7 text-xs"
                              >
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Rollback
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
            
            {/* Beta Testers Tab */}
            <TabsContent value="testers" className="space-y-4">
              <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
                <CardHeader>
                  <CardTitle className={cn("text-base", colors.textPrimary)}>Beta Testers</CardTitle>
                  <CardDescription className={colors.textMuted}>
                    Manage users who can access beta features. {betaCount} of {betaTotal} users are beta testers.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Search and Filter */}
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", colors.textMuted)} />
                      <Input
                        placeholder="Search by email, name..."
                        value={betaSearch}
                        onChange={(e) => setBetaSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <Select value={betaFilter} onValueChange={setBetaFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="beta">Beta Testers</SelectItem>
                        <SelectItem value="non-beta">Non-Beta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Users List */}
                  {betaLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {betaTesters.map((user) => (
                        <div
                          key={user.user_id}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg border",
                            colors.bgSecondary, colors.borderPrimary
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center",
                              user.is_beta_tester 
                                ? "bg-purple-100 dark:bg-purple-900/30" 
                                : colors.bgCard
                            )}>
                              {user.is_beta_tester ? (
                                <FlaskConical className="w-4 h-4 text-purple-500" />
                              ) : (
                                <User className={cn("w-4 h-4", colors.textMuted)} />
                              )}
                            </div>
                            <div>
                              <p className={cn("text-sm font-medium", colors.textPrimary)}>
                                {user.name || user.email}
                              </p>
                              <div className="flex items-center gap-2">
                                <p className={cn("text-xs", colors.textMuted)}>
                                  {user.email}
                                </p>
                                {user.organization_name && (
                                  <>
                                    <span className={colors.textMuted}>•</span>
                                    <p className={cn("text-xs", colors.textMuted)}>
                                      {user.organization_name}
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            {user.is_beta_tester && user.beta_tester_since && (
                              <span className={cn("text-[10px]", colors.textMuted)}>
                                Since {new Date(user.beta_tester_since).toLocaleDateString()}
                              </span>
                            )}
                            <Switch
                              checked={user.is_beta_tester || false}
                              onCheckedChange={() => toggleBetaTester(user.user_id, user.is_beta_tester)}
                              disabled={togglingUser === user.user_id}
                            />
                          </div>
                        </div>
                      ))}
                      
                      {betaTesters.length === 0 && (
                        <p className={cn("text-sm text-center py-8", colors.textMuted)}>
                          No users found matching your search
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default AdminFeaturesPage;
